import {Button, FormGroup, HTMLSelect} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {observer} from "mobx-react";
import * as React from "react";

import {UserRole} from "../../../api/values/user-role-type";
import {SubmitGroup} from "../../molecules/submit-group/submit-group";
import {ValidatedInputText} from "../../molecules/validated-input-text/validated-input-text";
import {AdminBreadcrumb} from "../../organisms/admin-breadcrumb/admin-breadcrumb";
import ErrorModal from "../../organisms/error-modal/error-modal";
import {AppLayout} from "../../templates/app-layout/app-layout";
import {User, UserEditPageStore} from "./user-edit-page-store";
import {UserNameInputWithDropdown} from "./user-name-input-with-dropdown";

function nameHelperText(pageStore: UserEditPageStore): string | undefined {
    if (pageStore.isExistingUser && pageStore.isNew) {
        return "";
    }
    const isValidInput = /^[a-z0-9]*$/.test(pageStore.editingName);

    if (!isValidInput) {
        return "a~z,0-9までの半角の小文字の英字、数字のみ使用できます";
    } else if (pageStore.isNameConflicted) {
        return "すでに利用されています";
    }

    return undefined;
}

const DeleteButton = styled.div`
    /* all: unset; */
    color: #790d0d;
    text-decoration: underline;
    margin-top: 20px;
    cursor: pointer;
`;

@observer
export class UserEditPage extends React.Component<
    {
        pageStore: UserEditPageStore;
    },
    {}
> {
    state = {
        isWarningModalOpen: false,
        pendingRole: null as UserRole | null, // 一時的に保持するロール
    };

    handleRoleChange = (evt: React.ChangeEvent<HTMLSelectElement>) => {
        // MEMO:
        // ロール変更時、値が取得できなかった場合writerとしているが
        // 早期returnの方が良いかもしれない
        const newRole = UserRole.fromName(evt.target.value) || UserRole.WRITER;

        // 新規ユーザー追加の場合はモーダルを表示しない
        if (this.props.pageStore.isNew) {
            this.props.pageStore.setRole(newRole);
            return;
        }

        // 現在のロールが ADMIN で、新しいロールが異なる場合、モーダルを表示
        if (this.props.pageStore.role.name === "ADMIN" && newRole.name !== "ADMIN") {
            this.setState({isWarningModalOpen: true, pendingRole: newRole});
        } else {
            this.props.pageStore.setRole(newRole);
        }
    };

    handleConfirmRoleChange = () => {
        this.setState({isWarningModalOpen: false});
        if (this.state.pendingRole) {
            this.props.pageStore.setRole(this.state.pendingRole);
        }
    };

    render() {
        const {pageStore} = this.props;

        const handleSelectUser = (user: User | null) => {
            if (user) {
                pageStore.setExistingUser(user);
            }
        };

        return (
            <AppLayout
                breadcrumb={
                    <AdminBreadcrumb
                        factory={(builder) =>
                            builder
                                .userList()
                                .custom(
                                    this.props.pageStore.isNew
                                        ? "ユーザー追加"
                                        : "ユーザー情報変更",
                                )
                                .build()
                        }
                    />
                }
            >
                <div style={{marginTop: "20px", width: "400px"}}>
                    <SubmitGroup
                        canSubmit={this.props.pageStore.canSave}
                        onSubmit={this.props.pageStore.save}
                    >
                        {pageStore.isNew ? (
                            <UserNameInputWithDropdown
                                value={pageStore.editingName}
                                onChangeValue={pageStore.setName}
                                onSelectUser={handleSelectUser}
                                userList={pageStore.userList}
                                isLoading={pageStore.isLoadingUserList}
                                onFocus={pageStore.fetchUserList}
                                validationResult={pageStore.nameValidationResult}
                                helperText={nameHelperText(pageStore)}
                                disabled={!pageStore.canEdit}
                                isNew={pageStore.isNew}
                            />
                        ) : (
                            <ValidatedInputText
                                label="ユーザー名"
                                type="text"
                                value={this.props.pageStore.editingName}
                                validationResult={this.props.pageStore.nameValidationResult}
                                onChangeValue={this.props.pageStore.setName}
                                validationInProgress={
                                    this.props.pageStore.isNameValidationInProgress
                                }
                                helperText={nameHelperText(this.props.pageStore)}
                                disabled={!this.props.pageStore.canEdit}
                            />
                        )}

                        <ValidatedInputText
                            label="メールアドレス"
                            type="text"
                            placeholder="aqua@example.com"
                            value={this.props.pageStore.email}
                            validationResult={this.props.pageStore.emailValidationResult}
                            onChangeValue={this.props.pageStore.setEmail}
                            disabled={!this.props.pageStore.canEdit || pageStore.isExistingUser}
                        />

                        <FormGroup label="ロール">
                            <HTMLSelect
                                value={this.props.pageStore.role.name}
                                onChange={this.handleRoleChange}
                                disabled={!this.props.pageStore.canEdit}
                                data-testid="role-select"
                            >
                                {this.props.pageStore.availableRoles.map((role) => (
                                    <option key={role.name} value={role.name}>
                                        {role.displayName}
                                    </option>
                                ))}
                            </HTMLSelect>
                        </FormGroup>

                        <div style={{marginBottom: "20px"}} />

                        <Button
                            type="submit"
                            text={this.props.pageStore.isNew ? "追加" : "変更"}
                            disabled={
                                !this.props.pageStore.canSave || !this.props.pageStore.canEdit
                            }
                            loading={this.props.pageStore.isSavingInProgress}
                            fill={true}
                        />
                        {!this.props.pageStore.isNew && (
                            <DeleteButton
                                onClick={() => {
                                    this.props.pageStore.isDeletingInProgress = true;
                                }}
                            >
                                メディアから削除
                            </DeleteButton>
                        )}
                    </SubmitGroup>
                </div>
                {/* ユーザー削除確認モーダル */}
                <ErrorModal
                    isOpen={this.props.pageStore.isDeletingInProgress}
                    onClose={() => (this.props.pageStore.isDeletingInProgress = false)}
                    onConfirm={() => this.props.pageStore.handleDeleteUser()}
                    title={"メディアから削除"}
                    message={`ユーザー${this.props.pageStore.editingName}を本当に削除しますか？`}
                    onCancel={() => (this.props.pageStore.isDeletingInProgress = false)}
                    confirmText="続行"
                />

                {/* 権限変更モーダル */}
                <ErrorModal
                    isOpen={this.state.isWarningModalOpen}
                    onClose={() => this.setState({isWarningModalOpen: false})}
                    onConfirm={this.handleConfirmRoleChange}
                    title={"権限変更の確認"}
                    message={
                        "システム管理者から他の権限に変更しようとしています。続行してもよろしいですか？"
                    }
                    onCancel={() => this.setState({isWarningModalOpen: false})}
                    confirmText="続行"
                />
            </AppLayout>
        );
    }
}
